import React, { useState, useEffect } from "react";
import { X, RefreshCw, Copy, Check, Info, Trash2, Cpu, Key, Lock, ShieldCheck, Tag, Globe } from "lucide-react";
import baseapi from "../../services/baseapi";
import { showError, showSuccess } from "../../utils/toast";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useClientLevel } from "../../hooks/useClientLevel";

interface ExternalApiSettingsProps {
    isOpen: boolean;
    onClose: () => void;
    onUninstall: () => void;
    integrationData?: any;
}

const ExternalApiSettings: React.FC<ExternalApiSettingsProps> = ({
    isOpen,
    onClose,
    onUninstall,
    integrationData
}) => {
    const level = useClientLevel();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [keyPrefix, setKeyPrefix] = useState<string>("");
    const [newCredentials, setNewCredentials] = useState<any>(null);
    const [copiedField, setCopiedField] = useState<string | null>(null);
    const [isRotating, setIsRotating] = useState(false);

    const parsedData = integrationData ? (
        typeof integrationData.integration_data === 'string'
            ? JSON.parse(integrationData.integration_data)
            : integrationData.integration_data
    ) : null;

    const fetchStatus = async () => {
        try {
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.get("integrations/external-api/status", { params });
            if (response.data.status) {
                setKeyPrefix(response.data.data.api_key_prefix);
            }
        } catch (error) {
            console.error("Failed to fetch integration status:", error);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchStatus();
            setNewCredentials(null);
        }
    }, [isOpen]);

    const handleRotateKey = async () => {
        setIsRotating(true);
        try {
            const payload: any = {};
            if (level && level.name !== "global" && level.id) {
                payload.client_id = level.id;
            }
            const response = await baseapi.post("integrations/external-api/rotate-key", payload);
            if (response.data.status) {
                setNewCredentials(response.data.data);
                showSuccess("Credentials rotated successfully. Store them securely.");
                fetchStatus();
            } else {
                showError("Failed to rotate credentials");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Failed to rotate credentials");
        } finally {
            setIsRotating(false);
        }
    };

    const handleUninstall = async () => {
        setIsConfirmOpen(false);
        try {
            const params: any = {};
            if (level && level.name !== 'global' && level.id) {
                params.client_id = level.id;
            }
            const response = await baseapi.delete("integrations/external-api/uninstall", { params });
            if (response.data.status) {
                showSuccess("Integration uninstalled successfully");
                onUninstall();
                onClose();
            } else {
                showError("Failed to uninstall");
            }
        } catch (error: any) {
            showError(error.response?.data?.message || "Failed to uninstall");
        }
    };

    const copyToClipboard = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300 p-4"
            onClick={handleBackdropClick}
        >
            <div className="relative bg-white rounded-[2rem] border border-slate-200 shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in duration-300 flex flex-col">
                {/* Premium Header */}
                <div className="bg-slate-50 px-6 py-5 flex items-center justify-between shrink-0 border-b border-slate-100 shadow-sm">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0 shadow-inner">
                            <Cpu className="text-[#422568] w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-base font-black text-slate-800 tracking-tight uppercase">API Configuration</h2>
                            <p className="text-slate-400 text-[9px] font-black uppercase tracking-[0.2em] leading-none mt-1.5 ">
                                Active Session
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-800 transition-all p-2 rounded-xl hover:bg-slate-100 border border-transparent hover:border-slate-200/50 active:scale-95"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh] bg-white text-slate-800">
                    {newCredentials ? (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
                            <div className="bg-[#422568] border-l-[3px] border-[#422568] p-4 rounded-r-2xl flex items-center gap-3">
                                <Info className="text-[#422568] shrink-0" size={18} />
                                <p className="text-[#422568] text-[10px] font-bold leading-relaxed">
                                    New credentials generated. Existing keys are now deprecated. Please update your external systems immediately.
                                </p>
                            </div>

                            <div className="space-y-4">
                                {/* API Key */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Key size={12} className="text-[#422568]" /> NEW API KEY
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={newCredentials.api_key}
                                            className="w-full pl-3 pr-10 py-3 bg-slate-50 text-slate-700 rounded-xl font-mono text-[9px] border border-slate-200 shadow-inner truncate font-semibold"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(newCredentials.api_key, 'key')}
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-slate-100 text-slate-655 border border-slate-200 rounded-lg shadow-sm transition-all active:scale-95"
                                        >
                                            {copiedField === 'key' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} className="text-slate-400" />}
                                        </button>
                                    </div>
                                </div>

                                {/* API Secret */}
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                        <Lock size={12} className="text-[#422568]" /> NEW API SECRET
                                    </label>
                                    <div className="relative group">
                                        <input
                                            type="text"
                                            readOnly
                                            value={newCredentials.api_secret}
                                            className="w-full pl-3 pr-10 py-3 bg-slate-50 text-slate-700 rounded-xl font-mono text-[9px] border border-slate-200 shadow-inner truncate font-semibold"
                                        />
                                        <button
                                            onClick={() => copyToClipboard(newCredentials.api_secret, 'secret')}
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-2 bg-white hover:bg-slate-100 text-slate-655 border border-slate-200 rounded-lg shadow-sm transition-all active:scale-95"
                                        >
                                            {copiedField === 'secret' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} className="text-slate-400" />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="space-y-4">
                                {/* Stats Row */}
                                <div className="grid grid-cols-2 gap-4">
                                    {/* Key Summary */}
                                    <div className="group p-4 bg-slate-50 rounded-3xl border border-slate-200 hover:border-[#422568]/20 hover:bg-white transition-all shadow-inner overflow-hidden relative flex flex-col justify-between">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-[#422568]/5 blur-3xl -mr-12 -mt-12 group-hover:bg-[#422568]/10 transition-all" />
                                        <div className="relative z-10">
                                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1.5 mb-2">
                                                <ShieldCheck size={12} className="text-emerald-500" /> Active Key
                                            </span>
                                            <span className="text-xs font-black text-[#422568] font-mono tracking-[0.2em]">
                                                {keyPrefix || parsedData?.key_hint || "••••••••"}
                                            </span>
                                        </div>
                                        <button
                                            onClick={handleRotateKey}
                                            disabled={isRotating}
                                            className="mt-3 w-full py-2 bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 rounded-xl transition-all font-bold text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-sm active:scale-95 disabled:opacity-50 relative z-10"
                                        >
                                            <RefreshCw size={12} className={isRotating ? "animate-spin" : ""} />
                                            Rotate
                                        </button>
                                    </div>

                                    {/* Endpoint Display */}
                                    <div className="group p-4 bg-slate-50 rounded-3xl border border-slate-200 hover:border-[#422568]/20 hover:bg-white transition-all shadow-inner overflow-hidden relative flex flex-col justify-between">
                                        <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/5 blur-3xl -mr-12 -mt-12 group-hover:bg-blue-500/10 transition-all" />
                                        <div className="relative z-10 overflow-hidden">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-1.5 mb-2">
                                                <Globe size={12} className="text-[#422568]" /> Endpoint
                                            </label>
                                            <div className="truncate text-[#422568] font-mono text-[10px] bg-white px-2 py-1.5 rounded-lg border border-slate-200 font-semibold">
                                                {localStorage.getItem('request_host') || window.location.origin}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(localStorage.getItem('request_host') || window.location.origin, 'url')}
                                            className="mt-3 w-full py-2 bg-white hover:bg-slate-50 text-slate-600 rounded-xl transition-all border border-slate-200 font-bold text-[9px] uppercase tracking-widest flex items-center justify-center gap-2 active:scale-95 relative z-10 shadow-sm"
                                        >
                                            {copiedField === 'url' ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} className="text-slate-400" />}
                                            {copiedField === 'url' ? 'Copied' : 'Copy URL'}
                                        </button>
                                    </div>
                                </div>

                                {/* Scopes Section */}
                                {parsedData?.scopes && parsedData.scopes.length > 0 && (
                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                                            <Tag size={12} className="text-[#422568]" /> Assigned Permissions
                                        </label>
                                        <div className="flex flex-wrap gap-1.5 p-3 bg-slate-50 rounded-2xl border border-slate-200 shadow-inner">
                                            {parsedData.scopes.map((scope: string) => (
                                                <span key={scope} className="px-2 py-0.5 bg-[#422568] border border-[#422568] text-[#422568] text-[8.5px] font-black uppercase tracking-wider rounded-lg shadow-sm animate-fade-in">
                                                    {scope.replace(':', ' ')}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Security Note */}
                                <div className="bg-slate-50 border border-slate-100 p-4 rounded-2xl">
                                    <p className="text-[9px] text-slate-400 font-semibold leading-relaxed italic text-center">
                                        These keys allow high-level access to system resources. Rotate them regularly and never share them across insecure channels.
                                    </p>
                                </div>

                                <button
                                    onClick={() => setIsConfirmOpen(true)}
                                    className="w-full py-3.5 bg-rose-50 hover:bg-rose-100/50 text-rose-600 rounded-2xl transition-all flex items-center justify-center gap-3 border border-rose-150 group active:scale-[0.98] font-bold text-[10px] uppercase tracking-[0.2em] shadow-sm animate-fade-in"
                                >
                                    <Trash2 size={16} className="group-hover:scale-110 transition-transform" />
                                    <span className="uppercase tracking-[.2em] text-[10px] font-black">Disconnect Integration</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
                    <button
                        onClick={onClose}
                        className="w-full py-3 bg-white hover:bg-slate-50 text-slate-600 font-bold rounded-xl transition-all border border-slate-200 text-[10px] uppercase tracking-[0.2em] active:scale-[0.98] shadow-sm"
                    >
                        Close Portal
                    </button>
                </div>
            </div>

            <ConfirmDialog
                isOpen={isConfirmOpen}
                onClose={() => setIsConfirmOpen(false)}
                onConfirm={handleUninstall}
                title="Disable Integration"
                message="Are you sure you want to disconnect this API integration? All active external services using these credentials will lose access immediately."
                confirmText="Disconnect"
                isDestructive={true}
            />
        </div>
    );
};

export default ExternalApiSettings;